import { c as _c } from "react/compiler-runtime";
import { IconPlay } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconChatBubbleOutline18, IconClockOutline18, IconCopyOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import ConfirmModal from "@/components/custom/ConfirmModal";
import { Badge } from "@/components/ui/badge";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu";
import { Button } from "@/components/ui/button";
import { TokenIcon } from "@/components/ui/token-icon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatRRule, formatTokenAmount } from "@/components/ui/workflow-step-row";
import { useDefinitionByType } from "@/hooks/useActionTriggerDefinitions";
import { useDescriptionFormatHints } from "@/hooks/useDescriptionFormatHints";
import { useFixWithAI } from "@/hooks/useFixWithAI";
import { useNodeExecutionStatus } from "@/hooks/useNodeExecutionStatus";
import { useResolvedNodeVisual } from "@/hooks/useResolvedNodeVisual";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { useTriggerInfo } from "@/hooks/useTriggerInfo";
import { cn } from "@/lib/utils";
import { useServerTimeOffset } from "@/hooks/useServerTimeOffset";
import { fromDateTimeLocalString, getDetailedCountdown, getRelativeTime } from "@/lib/utils/formatRelativeTime";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useNodeEditorStore } from "@/stores/nodeEditorStore";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import { useWorkflowRunsStore } from "@/stores/workflowRunsStore";
import type { WorkflowComment } from "@/types/workflow-comments";
import { camelToWords } from "@/utils/string";
import { useReactFlow } from "@xyflow/react";
import isEqual from "fast-deep-equal";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useAIHighlight } from "../_hooks/useAIHighlight";
import { AddressDisplay, isAddressField } from "./AddressDisplay";
import { useCompactView } from "./CompactViewContext";
import NodeCommentsBadge from "./NodeCommentsBadge";
import NodeExecutionDetails, { ErrorDetailModal, type NodeExecutionDetailsHandle } from "./NodeExecutionDetails";
import NodeOutputPreview from "./NodeOutputPreview";
// Performance optimization: Use TanStack Query for chain info caching
import NodeSelectionIndicator from "@/components/live/NodeSelectionIndicator";
import { containsTemplateVariable, inlineReferenceBadgeClassName, renderValueWithVariables } from "@/components/ui/workflow-step-row/components/VariablePill";
import { getSelectedOrgId } from "@/lib/authenticatedFetch";
import { listTelegramChats, type TelegramChat } from "@/lib/telegramBot";
import { jsonSchemaToWorkflowInputs } from "@/lib/workflow/inputSchema";
import type { WorkflowInput } from "@/stores/workflowsStore";
import { resolveDescriptionTemplates } from "@/utils/resolveDescriptionTemplates";
import { useQuery } from "@tanstack/react-query";
import { useChainInfo, useChainInfoMap } from "../_hooks/useChainInfo";
import { resolveSlackConversationLabel } from "./form-fields/slackChannels";
import { useSlackChannels } from "../_hooks/useSlackChannels";
import { useSlackUsers } from "../_hooks/useSlackUsers";
import { resolveTelegramChatLabel } from "./form-fields/telegramChats";
import { NodeDescriptionToolbar } from "./NodeDescriptionToolbar";
import { useNodeDescriptionVisible, useNodeHoverSetters } from "./NodeHoverContext";
import { getTriggerNodeSummary } from "./nodeSummaries";
import { NodeValidationErrorToolbar } from "./NodeValidationErrorToolbar";
import { usePropValues } from "./PropValuesContext";
import TriggerNodeWidgets from "./TriggerNodeWidgets";
import { TriggerNodeShell } from "./TriggerNodeShell";
import { useMultipleNodesSelected } from "./useMultipleNodesSelected";
import { useNodeValidationErrors } from "./ValidationErrorContext";

/**
 * Calculate duration between two timestamps
 */
const calculateDuration = (start: string | undefined, end: string | undefined): string => {
  if (!start || !end) return "";
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate.getTime() - startDate.getTime();
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  } catch {
    return "";
  }
};

/** Cap native tooltip text to avoid dumping huge values */
const tooltipText = (text: string, max = 100): string => text.length > max ? text.slice(0, max) + "…" : text;

// Constant for EVM Log Trigger type - used to identify and render special UI
const EVM_LOG_TRIGGER_TYPE = "evm-log";
const SCHEDULE_TRIGGER_TYPE = "schedule";
const CRONJOB_TRIGGER_TYPE = "cronjob";
const MANUAL_TRIGGER_TYPE = "manual";

/**
 * Rich display for schedule trigger output showing triggered_at with relative time.
 * Follows the same Clock-icon style as the schedule countdown display.
 */
function TriggeredAtPreview(t0) {
  const $ = _c(14);
  const {
    triggeredAt
  } = t0;
  const [relText, setRelText] = useState("");
  let t1;
  let t2;
  if ($[0] !== triggeredAt) {
    t1 = () => {
      const update = () => {
        const date = new Date(triggeredAt);
        if (isNaN(date.getTime())) {
          return;
        }
        setRelText(getRelativeTime(date));
      };
      update();
      const interval = setInterval(update, 10000);
      return () => clearInterval(interval);
    };
    t2 = [triggeredAt];
    $[0] = triggeredAt;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  bb0: {
    let t4;
    if ($[3] !== triggeredAt) {
      const date_0 = new Date(triggeredAt);
      if (isNaN(date_0.getTime())) {
        t3 = triggeredAt;
        break bb0;
      }
      t4 = date_0.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit"
      });
      $[3] = triggeredAt;
      $[4] = t4;
      $[5] = t3;
    } else {
      t4 = $[4];
      t3 = $[5];
    }
    t3 = t4;
  }
  const formatted = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IconClockOutline18 className="h-4 w-4 text-green-600 dark:text-green-400" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== formatted) {
    t5 = <p className="text-ds-text-primary text-xs font-medium">{formatted}</p>;
    $[7] = formatted;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== relText) {
    t6 = relText && <p className="text-ds-text-muted text-xs">{relText}</p>;
    $[9] = relText;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="border-ds-border-default border-t pt-3"><div className="flex items-center gap-2">{t4}<div className="flex-1">{t5}{t6}</div></div></div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
const INPUT_TYPE_LABELS: Record<WorkflowInput["type"], string> = {
  string: "Text",
  number: "Number",
  boolean: "Boolean",
  object: "Object",
  array: "Array"
};
function ManualInputSchemaPreview(t0) {
  const $ = _c(7);
  const {
    fields
  } = t0;
  if (fields.length > 5) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="label-xs">Input fields</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== fields.length) {
      t2 = <div className="flex items-center justify-between gap-2">{t1}<span className="micro-label text-ds-text-muted">{fields.length}</span></div>;
      $[1] = fields.length;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== fields) {
    t1 = fields.map(_temp);
    $[3] = fields;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="divide-ds-neutral-200/80 dark:divide-ds-neutral-800 divide-y">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp(field) {
  return <div key={field.key} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"><span className="body-xs text-ds-text-primary truncate font-medium">{field.key}</span><Badge variant="outline" className="py-0">{INPUT_TYPE_LABELS[field.type]}</Badge></div>;
}
interface TriggerNodeProps {
  data: {
    name?: string;
    type?: string;
    description?: string;
    platform?: string;
    payload?: Record<string, unknown>;
    connector?: unknown;
    titleOverride?: string;
    isTemplateView?: boolean;
    horizontalHandles?: boolean;
    hideSourceHandle?: boolean;
    onAddComment?: (nodeId: string) => void;
    onAddNodeComment?: (text: string) => void;
    nodeComments?: WorkflowComment[];
    onDeselectNode?: () => void;
    onRun?: () => void;
    runButtonLabel?: string;
    isStreamingNew?: boolean;
    isStreamingEdit?: boolean;
    isStreamingActive?: boolean;
    _refreshKey?: string;
  };
  selected?: boolean;
  id: string;
}

// Helper to format values for display
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * Converts milliseconds to a human-readable time format
 * Returns seconds if < 60s, minutes if < 60min, hours otherwise
 */
const formatDelayTime = (ms: number): string => {
  const seconds = ms / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (minutes < 60) {
    return `${minutes.toFixed(minutes % 1 === 0 ? 0 : 1)}m`;
  } else {
    return `${hours.toFixed(hours % 1 === 0 ? 0 : 1)}h`;
  }
};

// Memoized for performance - React Flow re-renders nodes frequently
function TriggerNodeComponent({
  data,
  selected,
  id
}: TriggerNodeProps) {
  const {
    deleteElements,
    getNode
  } = useReactFlow();
  const isCompactView = useCompactView();
  const searchParams = useSearchParams();
  const workflowId = searchParams.get("id") ?? undefined;
  const orgId = getSelectedOrgId();
  const propValues = usePropValues();
  const connector = data.connector as {
    id?: string;
    type?: string;
  } | undefined;
  const connectorId = connector?.id;
  const connectorType = connector?.type;

  // Performance optimization: Use TanStack Query for chain info caching
  // Extract chainIds for cache lookup
  const mainChainId = data.payload?.chainId as number | undefined;
  const {
    data: chainInfo
  } = useChainInfo(mainChainId);

  // Extract all chainIds from payload for batch fetch
  const payloadChainIds = useMemo(() => {
    if (!data.payload) return [];
    const ids: number[] = [];
    Object.entries(data.payload).forEach(([key, value]) => {
      if ((key.toLowerCase().includes("chainid") || key.toLowerCase().includes("chain_id")) && typeof value === "number") {
        ids.push(value);
      }
    });
    return ids;
  }, [data.payload]);
  const {
    chainInfoMap
  } = useChainInfoMap(payloadChainIds);

  // Extract tokenAddress from payload for token info lookup
  const tokenAddress = data.payload?.tokenAddress as string | undefined;

  // Fetch token info for triggers with tokenAddress + chainId
  const shouldFetchTokenInfo = !!(tokenAddress && mainChainId);
  const {
    data: simTokenData
  } = useSimTokenInfo(tokenAddress, mainChainId, shouldFetchTokenInfo);

  // Convert SimDune token data to display format
  const tokenInfo = useMemo(() => simTokenData?.tokens?.[0] ? {
    chainId: simTokenData.tokens[0].chain_id,
    address: simTokenData.contract_address,
    symbol: simTokenData.tokens[0].symbol,
    name: simTokenData.tokens[0].name || simTokenData.tokens[0].symbol,
    decimals: simTokenData.tokens[0].decimals,
    logoUrl: simTokenData.tokens[0].logo || simTokenData.tokens[0].token_metadata?.logo
  } : null, [simTokenData]);
  const descriptionFormatHints = useDescriptionFormatHints({
    simTokenData,
    simTokenInData: undefined,
    simSellTokenData: undefined,
    tokenInAddress: tokenAddress,
    tokenInChainId: mainChainId,
    isNativeOnlyAction: data.type === "eth-receive" || data.type === "eth-send",
    chainId: mainChainId
  });
  const hasSlackFields = typeof data.payload?.channelId === "string" || typeof data.payload?.channel === "string" || Array.isArray(data.payload?.conversations) || typeof data.payload?.conversation === "string";
  const {
    slackChannelsMap,
    isSlackConnector
  } = useSlackChannels(connectorId, connectorType, hasSlackFields);
  // Fetch Slack workspace users when the trigger references user IDs
  // (e.g. slack-new-user-mention payload.user = "U0855RQ3YQN") so the
  // summary can render avatar + display name instead of the raw ID.
  const hasSlackUserFields = typeof data.payload?.user === "string" && !!data.payload?.user;
  const {
    slackUsersMap
  } = useSlackUsers(connectorId, connectorType, hasSlackUserFields);
  const shouldFetchTelegramChats = !!connectorId && connectorType === "telegram-bot" && (typeof data.payload?.chatId === "string" || typeof data.payload?.chatId === "number");
  const {
    data: telegramChats = []
  } = useQuery<TelegramChat[]>({
    queryKey: ["telegram-chats", connectorId],
    queryFn: async () => (await listTelegramChats(undefined, 50, 0, connectorId!)).items,
    enabled: shouldFetchTelegramChats,
    staleTime: 5 * 60 * 1000
  });
  const telegramChatsMap = useMemo(() => new Map(telegramChats.map(chat => [String(chat.chatId), chat])), [telegramChats]);
  const clockOffset = useServerTimeOffset();
  const [relativeTime, setRelativeTime] = useState<string>("");
  const [countdown, setCountdown] = useState<string>("");
  const [contractName, setContractName] = useState<string | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const nodeRootRef = useRef<HTMLDivElement>(null);
  const nodeExecutionDetailsRef = useRef<NodeExecutionDetailsHandle>(null);

  // Track hover state globally for description toolbar visibility
  // PERFORMANCE: Use setters-only hook to avoid re-renders when other nodes are hovered
  const {
    setHoveredNodeId,
    setSelectedNodeId
  } = useNodeHoverSetters();
  const {
    showToolbar: showDescriptionToolbar,
    setToolbarHovered,
    canShowSelectedToolbar
  } = useNodeDescriptionVisible(id, isHovered, !!selected);
  const multipleNodesSelected = useMultipleNodesSelected();

  // Get validation errors for this node
  const validationErrors = useNodeValidationErrors(id);
  const isBeingConfigured = useNodeEditorStore(s => s.newlyAddedNodeId) === id;
  const hasValidationErrors = validationErrors.length > 0 && !isBeingConfigured;

  // Update selected node in context when selection changes
  useEffect(() => {
    if (selected) {
      setSelectedNodeId(id);
    } else {
      setSelectedNodeId(null);
      // Clear newly-added flag when user navigates away from this node
      if (useNodeEditorStore.getState().newlyAddedNodeId === id) {
        useNodeEditorStore.getState().setNewlyAddedNodeId(null);
      }
    }
  }, [selected, id, setSelectedNodeId]);

  // Get the trigger definition to check if it requires a connector
  const definition = useDefinitionByType(data.type as string);
  const iconVisual = useResolvedNodeVisual(data.type);

  // Get connector logo URL if definition requires a connector
  // PERF: useShallow prevents all N nodes re-rendering when connectorTypes array ref changes
  const connectorTypes = useConnectorsStore(useShallow(state => state.connectorTypes));
  const connectorLogoUrl = definition?.connector?.type ? connectorTypes.find(ct => ct.type === definition.connector?.type)?.logoUrl : undefined;
  // PERF: Memoize connector prop to avoid new object on every render
  const connectorProp = useMemo(() => definition?.connector?.type ? {
    type: definition.connector.type,
    logoUrl: connectorLogoUrl
  } : connectorType ? {
    type: connectorType
  } : undefined, [definition?.connector?.type, connectorLogoUrl, connectorType]);

  // "Fix with AI" functionality for failed workflow runs
  const handleFixWithAI = useFixWithAI(data.onDeselectNode);

  // Track AI update highlight effect
  const showAIHighlight = useAIHighlight(data._refreshKey);

  // Check if this is an EVM Log Trigger using exact type match for robustness
  const isEvmLogTrigger = data.type === EVM_LOG_TRIGGER_TYPE;
  const isScheduleTrigger = data.type === SCHEDULE_TRIGGER_TYPE;
  const isCronjobTrigger = data.type === CRONJOB_TRIGGER_TYPE;
  const isManualTrigger = data.type === MANUAL_TRIGGER_TYPE;

  // Fetch trigger info for anyspend triggers that have enablePaymentLink set
  const isAnyspendTrigger = data.type === "anyspend";
  const enablePaymentLink = data.payload?.enablePaymentLink === true;
  const {
    triggerInfo: anyspendTriggerInfo,
    isLoading: isLoadingTriggerInfo
  } = useTriggerInfo(workflowId, isAnyspendTrigger && enablePaymentLink);
  const paymentLinkUrl = anyspendTriggerInfo?.paymentLinkUrl ?? null;
  const chainId = data.payload?.chainId as number | undefined;
  const contractAddress = data.payload?.contractAddress as string | undefined;
  const eventAbi = data.payload?.eventAbi as string | undefined;
  const filter = data.payload?.filter as string | undefined;
  const nextRunAt = data.payload?.nextRunAt as string | undefined;

  // Parse event name from ABI
  let eventName = "";
  if (eventAbi) {
    try {
      const parsed = typeof eventAbi === "string" ? JSON.parse(eventAbi) : eventAbi;
      if (parsed.name) {
        eventName = parsed.name;
      } else if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
        eventName = parsed[0].name;
      }
    } catch {
      // Invalid JSON, ignore
    }
  }

  // Note: Chain info is now fetched via useChainInfo hook above (TanStack Query)

  // Fetch contract name if we have chainId and contractAddress
  useEffect(() => {
    if (chainId && contractAddress && contractAddress.startsWith("0x") && contractAddress.length === 42) {
      const controller = new AbortController();
      fetch(`https://contract-abi-api.b3.fun/list?chainId=${chainId}&limit=100`, {
        signal: controller.signal
      }).then(res => res.json()).then((response: {
        results?: Array<{
          address: string;
          name?: string;
          nameTag?: string;
        }>;
      }) => {
        const contract_0 = response.results?.find(contract => contract.address.toLowerCase() === contractAddress.toLowerCase());
        if (contract_0) {
          setContractName(contract_0.name || contract_0.nameTag || null);
        } else {
          setContractName(null);
        }
      }).catch(err => {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("Failed to fetch contract name:", err);
        setContractName(null);
      });
      return () => controller.abort();
    } else {
      setContractName(null);
    }
  }, [chainId, contractAddress]);

  // Update relative time and countdown for schedule triggers
  useEffect(() => {
    if (!isScheduleTrigger || !nextRunAt) {
      setRelativeTime("");
      setCountdown("");
      return;
    }
    const targetDate = fromDateTimeLocalString(nextRunAt);
    if (!targetDate) {
      setRelativeTime("");
      setCountdown("");
      return;
    }
    const updateTime = () => {
      setRelativeTime(getRelativeTime(targetDate, clockOffset));
      setCountdown(getDetailedCountdown(targetDate, clockOffset));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [isScheduleTrigger, nextRunAt, clockOffset]);

  // Note: Chain info for payload chainIds is now fetched via useChainInfoMapCached hook above

  // Filter out empty/undefined payload values and hide abi fields
  const configuredValues = Object.entries(data.payload || {}).filter(([key_0, value_0]) => {
    // Hide abi fields since we show functionName/eventName instead
    if (key_0 === "abi" || key_0 === "eventAbi") return false;
    if (value_0 === null || value_0 === undefined || value_0 === "") return false;
    if (Array.isArray(value_0) && value_0.length === 0) return false;
    return true;
  });
  const triggerSummary = useMemo(() => getTriggerNodeSummary(data.type, data.payload, {
    chainInfoMap,
    tokenInfo,
    slackChannelsMap,
    slackUsersMap,
    telegramChatsMap
  }), [chainInfoMap, data.payload, data.type, slackChannelsMap, slackUsersMap, telegramChatsMap, tokenInfo]);
  const manualInputFields = useMemo(() => {
    if (!isManualTrigger) return [];
    return jsonSchemaToWorkflowInputs(data.payload?.inputSchema);
  }, [data.payload?.inputSchema, isManualTrigger]);
  const remainingConfiguredValues = useMemo(() => {
    if (!triggerSummary?.consumedKeys?.length) {
      return configuredValues.filter(([key_1]) => !(manualInputFields.length > 0 && key_1 === "inputSchema"));
    }
    const consumed = new Set(triggerSummary.consumedKeys);
    return configuredValues.filter(([key_2]) => !consumed.has(key_2) && !(manualInputFields.length > 0 && key_2 === "inputSchema"));
  }, [configuredValues, manualInputFields.length, triggerSummary]);
  const shouldShowGenericTriggerSection = manualInputFields.length > 0 || remainingConfiguredValues.length > 0 || !!triggerSummary && !isScheduleTrigger && !isCronjobTrigger && !isManualTrigger;

  // Get execution status for this node using API node ID (server sends events with API IDs)
  const executionNodeId = (data as any).apiNodeId as string || id;
  const {
    status: executionStatus,
    hasActiveRun,
    nodeState,
    runId
  } = useNodeExecutionStatus(executionNodeId);
  const runWorkflowId = useRunExecutionStore(state_0 => state_0.currentRun?.workflowId);
  const runsStoreWorkflowId = useWorkflowRunsStore(state_1 => state_1.currentWorkflowId);
  const currentWorkflowId = runWorkflowId || runsStoreWorkflowId;

  // Determine if we should show output preview instead of settings
  const showOutputPreview = hasActiveRun && (executionStatus === "success" || executionStatus === "failure") && nodeState?.result && Object.keys(nodeState.result).length > 0;
  const handleDelete = useCallback(() => {
    // Delay to let ContextMenu fully close before opening Dialog
    // Prevents pointer-events conflicts between the two Radix components
    setTimeout(() => setShowDeleteConfirm(true), 0);
  }, []);
  const confirmDelete = useCallback(() => {
    // Close dialog first, then delete on the next tick. See LogicNode for
    // the full explanation of the Radix DismissableLayer pointer-events race.
    setShowDeleteConfirm(false);
    setTimeout(() => {
      deleteElements({
        nodes: [{
          id
        }]
      });
      requestAnimationFrame(() => {
        if (document.body.style.pointerEvents === "none") {
          document.body.style.removeProperty("pointer-events");
        }
      });
    }, 0);
  }, [deleteElements, id]);
  const handleDuplicate = useCallback(() => {
    const node = getNode(id);
    if (node) {
      // TODO: Implement duplicate functionality
      console.log("Duplicate node:", node);
    }
  }, [getNode, id]);
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setHoveredNodeId(id);
  }, [id, setHoveredNodeId]);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setHoveredNodeId(null);
  }, [setHoveredNodeId]);

  // Memoize nodeInfo object to prevent unnecessary re-renders
  const nodeInfo = useMemo(() => ({
    name: data.name,
    type: data.type,
    description: data.description,
    category: "trigger" as const
  }), [data.name, data.type, data.description]);

  // Determine if we should show the rotating border effect
  const canShowAnimatedBorder = !showAIHighlight && (!hasActiveRun || executionStatus === "idle") && !hasValidationErrors;
  const showRotatingBorder = selected && canShowAnimatedBorder;
  const showHoverBorder = isHovered && !selected && canShowAnimatedBorder;
  const streamingClass = data.isStreamingNew ? "streaming-enter" : data.isStreamingEdit ? "streaming-edit-highlight" : undefined;
  const isStreamingActive = !!data.isStreamingActive;
  const displayName = resolveDescriptionTemplates(data.titleOverride, data.payload) || data.name || data.type || "Trigger";
  const iconSubject = data.name || data.type || "Trigger";
  const iconContext = resolveDescriptionTemplates(data.description, data.payload) || "";
  const iconAlt = data.name || "Trigger";
  const resolvedConnector = connectorProp ?? iconVisual.connector;
  const subtitleNode = <div className="micro-label flex h-5 items-center gap-1">
      <span>{showOutputPreview ? "Trigger output" : "Trigger"}</span>
      {showOutputPreview && <>
          <span>·</span>
          <span className={cn(executionStatus === "success" && "text-green-600 dark:text-green-400", executionStatus === "failure" && "text-red-600 dark:text-red-400")}>
            {calculateDuration(nodeState?.startedAt, nodeState?.finishedAt) || "Done"}
          </span>
        </>}
    </div>;
  const toolbarSlot = <>
      <NodeDescriptionToolbar description={data.description} errorMessage={executionStatus === "failure" ? nodeState?.error : undefined} defaultDescription={definition?.description} isVisible={!multipleNodesSelected && (showDescriptionToolbar || isCompactView && canShowSelectedToolbar) && !hasValidationErrors} nodeName={data.name} nodeId={id} onInspect={hasActiveRun && executionStatus !== "idle" ? () => nodeExecutionDetailsRef.current?.openInspectModal() : undefined} onComment={data.onAddComment ? () => data.onAddComment?.(id) : undefined} onDuplicate={handleDuplicate} onDelete={handleDelete} onHoverChange={setToolbarHovered} nodeRef={nodeRootRef} tokens={tokenInfo ? [{
      address: tokenInfo.address,
      symbol: tokenInfo.symbol,
      name: tokenInfo.name,
      chainId: tokenInfo.chainId,
      logoUrl: tokenInfo.logoUrl
    }] : undefined} payload={data.payload} formatHints={descriptionFormatHints} />
      <NodeValidationErrorToolbar errors={validationErrors} isVisible={hasValidationErrors && (isHovered || !!selected)} nodeName={data.name} nodeId={id} nodeRef={nodeRootRef} onDeselectNode={data.onDeselectNode} />
    </>;
  const bannerSlot = <>
      {isScheduleTrigger && nextRunAt && relativeTime && <div className="border-ds-border-default border-t pt-3">
          <div className="flex items-center gap-2">
            <IconClockOutline18 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <div className="flex-1">
              <p className="text-ds-text-primary text-xs font-medium">{relativeTime}</p>
              {countdown && countdown !== "Expired" && <p className="text-ds-text-subtle dark:text-ds-text-disabled font-mono text-xs">{countdown}</p>}
            </div>
          </div>
          {countdown === "Expired" && <div className="mt-2 rounded-md bg-red-50 px-2 py-1 dark:bg-red-950/20">
              <p className="text-xs text-red-700 dark:text-red-400">This trigger has expired</p>
            </div>}
        </div>}

      {!data.isTemplateView && isManualTrigger && data.onRun && <div className="border-ds-border-default border-t pt-3">
          <Button onClick={e => {
        e.stopPropagation();
        data.onRun?.();
      }} className="w-full">
            <span className="flex items-center gap-2 text-xs">
              <IconPlay className="h-3 w-3" />
              {data.runButtonLabel ?? "Run Workflow"}
            </span>
          </Button>
        </div>}

      {!data.isTemplateView && (isScheduleTrigger || isCronjobTrigger) && data.onRun && <div className={cn(!isScheduleTrigger || !nextRunAt || !relativeTime ? "border-ds-border-default border-t pt-3" : "pt-2")}>
          <Button variant="white" onClick={e_0 => {
        e_0.stopPropagation();
        data.onRun?.();
      }} className="w-full">
            <span className="flex items-center gap-2 text-xs">
              <IconPlay className="h-3 w-3" />
              Trigger Now
            </span>
          </Button>
        </div>}

      {!data.isTemplateView && <TriggerNodeWidgets triggerType={data.type} payload={data.payload} propValues={propValues} workflowId={workflowId} orgId={orgId} paymentLinkUrl={paymentLinkUrl} isLoadingTriggerInfo={isLoadingTriggerInfo} />}
    </>;
  const bodySlot = showOutputPreview ? isScheduleTrigger && nodeState?.result?.triggered_at ? <TriggeredAtPreview triggeredAt={String(nodeState.result.triggered_at)} /> : <NodeOutputPreview result={nodeState?.result} status={executionStatus} /> : isEvmLogTrigger ? <div className="border-ds-border-default space-y-2 border-t pt-3">
      {/* Chain Info */}
      {chainInfo && <div className="flex items-center gap-2">
          {chainInfo.iconUrl && <img src={chainInfo.iconUrl} alt={chainInfo.name} className="h-4 w-4 rounded-full" />}
          <span className="body-sm-medium">{chainInfo.name}</span>
        </div>}

      {/* Contract Address */}
      {contractAddress && <div className="flex items-start gap-2">
          <span className="label-xs-caps">Contract:</span>
          <span className="text-ds-text-secondary flex-1 truncate text-xs" title={contractAddress}>
            {contractName ? <>
                <span className="font-medium">{contractName}</span>
                <span className="text-ds-text-muted dark:text-ds-text-muted ml-1 font-mono">
                  ({contractAddress.slice(0, 4)}...{contractAddress.slice(-4)})
                </span>
              </> : <span className="font-mono">
                {contractAddress.slice(0, 6)}...{contractAddress.slice(-4)}
              </span>}
          </span>
        </div>}

      {/* Event Name */}
      {eventName && <div className="flex items-start gap-2">
          <span className="label-xs-caps">Event:</span>
          <span className="flex-1 font-mono text-xs font-medium text-blue-600 dark:text-blue-400">{eventName}</span>
        </div>}

      {/* Filter */}
      {filter && <div className="flex items-start gap-2">
          <span className="label-xs-caps">Filter:</span>
          <span className="text-ds-text-secondary flex-1 truncate font-mono text-xs" title={filter}>
            {typeof filter === "string" ? filter.length > 30 ? `${filter.slice(0, 30)}...` : filter : "Configured"}
          </span>
        </div>}
    </div> : shouldShowGenericTriggerSection ? <div className="border-ds-border-default space-y-1.5 border-t pt-2">
      {manualInputFields.length > 0 && <ManualInputSchemaPreview fields={manualInputFields} />}
      {!isScheduleTrigger && !isCronjobTrigger && !isManualTrigger && triggerSummary && <div className="space-y-1">
          <div className="body-xs leading-5">{triggerSummary.summary}</div>
          {triggerSummary.auxiliary && <div className="label-xs">{triggerSummary.auxiliary}</div>}
        </div>}
      {remainingConfiguredValues.map(([key_3, value_1]) => {
      const displayValue = formatValue(value_1);
      let label = camelToWords(key_3);
      const isRRule = key_3 === "rrule" && typeof value_1 === "string";
      const isDelay = (key_3.toLowerCase().includes("delay") || key_3.toLowerCase().includes("ms")) && typeof value_1 === "number";
      const isChainId = (key_3.toLowerCase().includes("chainid") || key_3.toLowerCase().includes("chain_id")) && typeof value_1 === "number";
      const isSlackChannel = (key_3 === "channelId" || key_3 === "channel" || key_3 === "conversation") && typeof value_1 === "string" && isSlackConnector;
      const isSlackChannels = key_3 === "conversations" && Array.isArray(value_1) && value_1.every(item => typeof item === "string") && isSlackConnector;
      const isTelegramChat = key_3 === "chatId" && (typeof value_1 === "string" || typeof value_1 === "number");

      // Special label formatting for chain IDs
      if (isChainId) {
        label = "Chain";
      }
      if (isSlackChannel) {
        label = "Channel";
      }
      if (isSlackChannels) {
        label = "Channels";
      }
      if (isTelegramChat) {
        label = "Chat";
      }

      // Special handling for chain IDs - show as badge with icon
      if (isChainId) {
        const chain = chainInfoMap.get(value_1 as number);
        return <div key={key_3} className="flex items-start gap-2 text-xs">
              <span className="text-ds-text-subtle dark:text-ds-text-disabled inline-flex min-h-6 min-w-0 flex-shrink-0 items-center font-medium capitalize">
                {label}:
              </span>
              {chain ? <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="inline-flex cursor-help items-center gap-1 truncate">
                        {chain.iconUrl && <img src={chain.iconUrl} alt={chain.name} className="h-3 w-3 flex-shrink-0 rounded-full" />}
                        <span className="text-ds-text-primary truncate text-xs font-medium">{chain.name}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Chain ID: {chain.chainId}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> : <span className="text-ds-text-secondary truncate" title={tooltipText(displayValue)}>
                  {displayValue}
                </span>}
            </div>;
      }
      if (isSlackChannel) {
        const channelLabel = resolveSlackConversationLabel(value_1, slackChannelsMap) || "Slack channel";
        return <div key={key_3} className="flex items-start gap-2 overflow-hidden">
              <span className="label-xs inline-flex min-h-6 min-w-0 flex-shrink-0 items-center capitalize">
                {label}:
              </span>
              <span className={cn(inlineReferenceBadgeClassName, "max-w-full")}>{channelLabel}</span>
            </div>;
      }
      if (isSlackChannels) {
        return <div key={key_3} className="flex items-start gap-2 overflow-hidden">
              <span className="label-xs inline-flex min-h-6 min-w-0 flex-shrink-0 items-center capitalize">
                {label}:
              </span>
              <div className="body-sm flex min-w-0 flex-1 flex-wrap gap-1.5">
                {(value_1 as string[]).map(item_0 => <span key={item_0} className={cn(inlineReferenceBadgeClassName, "max-w-full")}>
                    {resolveSlackConversationLabel(item_0, slackChannelsMap) || "Slack channel"}
                  </span>)}
              </div>
            </div>;
      }
      if (isTelegramChat) {
        const chatLabel = resolveTelegramChatLabel(String(value_1), telegramChatsMap) || "Telegram chat";
        return <div key={key_3} className="flex items-start gap-2 overflow-hidden">
              <span className="label-xs inline-flex min-h-6 min-w-0 flex-shrink-0 items-center capitalize">
                {label}:
              </span>
              <span className={cn(inlineReferenceBadgeClassName, "max-w-full")}>{chatLabel}</span>
            </div>;
      }

      // Special handling for tokenAddress - show token info with icon
      if (key_3 === "tokenAddress" && tokenInfo) {
        return <div key={key_3} className="flex items-center gap-2 text-xs">
              <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium">
                Token:
              </span>
              <div className="flex items-center gap-1">
                <TokenIcon logoUrl={tokenInfo.logoUrl} symbol={tokenInfo.symbol} chainId={tokenInfo.chainId} tokenAddress={tokenInfo.address} size={14} />
                <span className="text-ds-text-secondary text-xs font-medium">
                  {tokenInfo.symbol} - {tokenInfo.name}
                </span>
              </div>
            </div>;
      }

      // Special handling for address fields (accountAddress, walletAddress, etc.) - show ENS avatar
      if (isAddressField(key_3, value_1)) {
        return <AddressDisplay key={key_3} address={value_1 as string} label={label} size="sm" />;
      }

      // Special handling for RRULE - display on own line
      if (isRRule) {
        return <div key={key_3} className="space-y-0.5">
              <div className="flex items-start gap-2 text-xs">
                <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">
                  Schedule:
                </span>
              </div>
              <div className="text-ds-text-muted pl-0 text-xs italic">{formatRRule(value_1 as string)}</div>
            </div>;
      }

      // Special handling for wei amount fields - format to human-readable
      const isWeiAmount = (key_3 === "minAmount" || key_3 === "amount") && typeof value_1 === "string" && /^\d+$/.test(value_1);
      if (isWeiAmount) {
        const decimals = tokenInfo?.decimals ?? 18;
        const symbol = tokenInfo?.symbol ?? "ETH";
        const formatted = formatTokenAmount(value_1, decimals);
        return <div key={key_3} className="flex items-center gap-2 overflow-hidden">
              <span className="label-xs min-w-0 flex-shrink-0 capitalize">{label}:</span>
              <span className="body-xs min-w-0 flex-1 truncate" title={`${formatted} ${symbol} (${value_1} wei)`}>
                {formatted} {symbol}
              </span>
            </div>;
      }

      // Regular fields with optional inline extra info
      const extraInfo = isDelay ? ` (${formatDelayTime(value_1 as number)})` : "";
      return <div key={key_3} className="flex items-center gap-2 overflow-hidden">
            <span className="label-xs min-w-0 flex-shrink-0 capitalize">{label}:</span>
            {containsTemplateVariable(value_1) ? <span className="body-xs flex min-w-0 flex-1 flex-wrap items-center gap-0.5 overflow-hidden">
                {renderValueWithVariables(value_1)}
              </span> : <span className="body-xs min-w-0 flex-1 truncate" title={tooltipText(displayValue + extraInfo)}>
                {displayValue}
                {extraInfo && <span className="text-ds-text-muted">{extraInfo}</span>}
              </span>}
          </div>;
    })}
    </div> : null;
  const executionDetailsSlot = hasActiveRun && executionStatus !== "idle" && executionStatus !== "running" ? <NodeExecutionDetails ref={nodeExecutionDetailsRef} nodeState={nodeState} nodeInfo={nodeInfo} onFixWithAI={handleFixWithAI} workflowId={currentWorkflowId} runId={runId} /> : null;
  return <ContextMenu>
      <ContextMenuTrigger asChild>
        <TriggerNodeShell id={id} dataApiNodeId={executionNodeId} name={displayName} subtitle={subtitleNode} iconUrl={iconVisual.logoUrl} connectorIconUrl={resolvedConnector?.logoUrl} connectorType={resolvedConnector?.type} iconId={data.type || "trigger"} iconSubject={iconSubject} iconContext={iconContext} iconAlt={iconAlt} status={hasActiveRun ? executionStatus : "idle"} selected={selected} hasValidationErrors={hasValidationErrors} isStreamingActive={isStreamingActive} showAIHighlight={showAIHighlight} showRotatingBorder={showRotatingBorder} showHoverBorder={showHoverBorder} streamingClass={streamingClass} hideSourceHandle={data.hideSourceHandle} horizontalHandles={data.horizontalHandles} compact={isCompactView} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} nodeRef={nodeRootRef} toolbarSlot={toolbarSlot} selectionIndicatorSlot={<NodeSelectionIndicator nodeId={id} />} bannerSlot={bannerSlot} bodySlot={bodySlot} executionDetailsSlot={executionDetailsSlot} commentsBadgeSlot={data.nodeComments ? <NodeCommentsBadge nodeComments={data.nodeComments} onAddComment={data.onAddNodeComment} /> : null} onErrorBadgeClick={() => setErrorModalOpen(true)} />
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem onClick={handleDuplicate} className="cursor-pointer">
          <IconCopyOutline18 className="mr-2 h-4 w-4" />
          Duplicate
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => data.onAddComment?.(id)} className="cursor-pointer">
          <IconChatBubbleOutline18 className="mr-2 h-4 w-4" />
          Add Comment
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleDelete} className="cursor-pointer text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400">
          <IconTrashOutline18 className="mr-2 h-4 w-4" />
          Delete Trigger
        </ContextMenuItem>
      </ContextMenuContent>
      {/* Error Detail Modal - opens when clicking on failed status badge */}
      <ErrorDetailModal open={errorModalOpen} onOpenChange={setErrorModalOpen} nodeInfo={nodeInfo} nodeState={nodeState} nodeConfig={data.payload} onFixWithAI={handleFixWithAI} workflowId={currentWorkflowId} runId={runId} />
      {/* Delete Confirmation Modal */}
      <ConfirmModal open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm} title="Delete trigger?" description="This will remove the trigger from your workflow." confirmText="Delete" variant="destructive" onConfirm={confirmDelete} />
    </ContextMenu>;
}

// Custom comparator for React.memo - shallow compare primitives, deep compare objects
function arePropsEqual(prevProps: TriggerNodeProps, nextProps: TriggerNodeProps): boolean {
  if (prevProps.id !== nextProps.id || prevProps.selected !== nextProps.selected) {
    return false;
  }
  const prevData = prevProps.data;
  const nextData = nextProps.data;
  if (prevData === nextData) return true;
  if (!prevData || !nextData) return false;
  if (prevProps.data.isStreamingNew !== nextProps.data.isStreamingNew || prevProps.data.isStreamingEdit !== nextProps.data.isStreamingEdit || prevProps.data.isStreamingActive !== nextProps.data.isStreamingActive) {
    return false;
  }
  return prevData.name === nextData.name && prevData.type === nextData.type && prevData.description === nextData.description && prevData.platform === nextData.platform && prevData.isTemplateView === nextData.isTemplateView && prevData.runButtonLabel === nextData.runButtonLabel && isEqual(prevData.payload, nextData.payload) && isEqual(prevData.nodeComments, nextData.nodeComments);
}

// React.memo prevents re-renders when props haven't changed
// This is important for React Flow performance with many nodes
const TriggerNode = React.memo(TriggerNodeComponent, arePropsEqual);
export default TriggerNode;